import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import { formatDate } from '../utils/helpers';

const MassEmailHistory = () => {
  const { massEmailCampaigns } = useCRM();
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCampaigns = massEmailCampaigns.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-slate-200">
        <div className="space-y-2">
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">E-post Kampanjer</h1>
          <p className="text-slate-500 font-medium text-lg">Se historikk og ytelse for dine utsendelser.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Totalt Sendt', value: massEmailCampaigns.reduce((acc, c) => acc + c.stats.sent, 0), icon: "📤", color: 'blue' },
          { label: 'Åpningsrate', value: '32%', icon: "✅", color: 'emerald' },
          { label: 'Klikkrate', value: '12%', icon: "🖱️", color: 'sky' },
          { label: 'Svarrate', value: '4%', icon: "📧", color: 'rose' }
        ].map((stat, idx) => (
          <div key={idx} className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm relative overflow-hidden group">
            <div className={`absolute top-0 right-0 w-24 h-24 bg-${stat.color}-50 rounded-bl-full -mr-12 -mt-12 group-hover:scale-110 transition-transform duration-500 opacity-50`} />
            <div className={`w-12 h-12 bg-${stat.color}-50 rounded-2xl flex items-center justify-center text-${stat.color}-600 mb-6 border border-${stat.color}-100`}>
              <SafeIcon icon={stat.icon} className="w-6 h-6" />
            </div>
            <p className="text-xs font-black text-slate-400 uppercase tracking-widest">{stat.label}</p>
            <p className="text-3xl font-black text-slate-900 mt-2">{stat.value}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-[2.5rem] border border-slate-200 overflow-hidden shadow-sm">
        <div className="p-8 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="relative w-80">
            <input type="text" placeholder="Søk i kampanjer..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-[1.25rem] text-sm font-bold focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none transition-all" />
            <SafeIcon icon="🔍" className="absolute left-4 top-4.5 w-5 h-5 text-slate-400" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Kampanje</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Sendt Dato</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Mottakere</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Ytelse</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Status</th>
                <th className="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredCampaigns.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-slate-50/50 transition-colors group">
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600 border border-primary-100">
                        <SafeIcon icon="📧" className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-black text-slate-900">{campaign.name}</p>
                        <p className="text-xs font-bold text-slate-400 mt-0.5">Avsender: {campaign.sender}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <p className="text-sm font-bold text-slate-600">{formatDate(campaign.sent_at)}</p>
                  </td>
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-2">
                      <SafeIcon icon="👥" className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-black text-slate-900">{campaign.stats.sent}</span>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Åpnet</p>
                        <p className="text-sm font-black text-emerald-600">{campaign.stats.opened}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Klikket</p>
                        <p className="text-sm font-black text-sky-600">{campaign.stats.clicked}%</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-8 py-6">
                    <span className="px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase tracking-widest border border-emerald-100"> Fullført </span>
                  </td>
                  <td className="px-8 py-6 text-right">
                    <button className="p-2 text-slate-400 hover:text-slate-900 transition-colors">
                      <SafeIcon icon="⋮" className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {filteredCampaigns.length === 0 && (
                <tr>
                  <td colSpan="6" className="px-8 py-24 text-center">
                    <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
                      <SafeIcon icon="📧" className="w-8 h-8 text-slate-200" />
                    </div>
                    <p className="text-slate-500 font-bold">Ingen kampanjer funnet.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MassEmailHistory;